import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { MDBTable, MDBIcon, MDBBtn, MDBBtnGroup, MDBBadge } from "mdbreact";

import {
  SetSELECTED,
  DESTROY,
  RESET,
} from "../../../../../services/redux/slices/assets/providers";
import Swal from "sweetalert2";
import {
  currency,
  dateFormat,
  mobile,
} from "../../../../../services/utilities";

const formatAddress = (address) =>
  typeof address === "string"
    ? address
    : [address?.street, address?.barangay, address?.city, address?.province]
        .filter(Boolean)
        .join(", ");

// live details of the supplier branch, the tag only keeps a copy from when it was tagged
const details = (supplier = {}) => {
  const branch = supplier.vendors || {},
    company = branch.companyId || {};
  return {
    company: company.name || supplier.company || supplier.name || "",
    branch:
      branch.displayname || branch.name || supplier.displayname || "",
    abbr: branch.abbr || company.abbr || supplier.abbr || "",
    isMain: !!branch.isMain,
    isGhost: !!company.isGhost,
    number:
      branch.contacts?.mobile || company.contacts?.mobile || supplier.number,
    email: branch.contacts?.email || company.contacts?.email || "",
    address: formatAddress(branch.address) || supplier.address || "",
  };
};

const Muted = ({ children = "—" }) => (
  <span className="text-muted">{children}</span>
);

const Body = () => {
  const { token } = useSelector(({ auth }) => auth),
    { filtered, activePage, maxPage, formSubmitted, isSuccess } = useSelector(
      ({ providers }) => providers,
    ),
    dispatch = useDispatch();

  useEffect(() => {
    if (!formSubmitted && isSuccess) dispatch(RESET());
  }, [formSubmitted, isSuccess, dispatch]);

  const handleEdit = (supplier) => {
    dispatch(SetSELECTED(supplier));
  };

  const handleDelete = (_id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "The supplier will be untagged from this branch; it stays registered and can be tagged again.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, untag it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(DESTROY({ token, data: { _id } }));
      }
    });
  };

  /**
   * Pagination: Calculate the start and end index for the current page
   */
  const itemsPerPage = maxPage; // Number of items per page
  const startIndex = (activePage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filtered.slice(startIndex, endIndex); // Get only items for the active page

  return (
    <MDBTable responsive hover bordered>
      <thead>
        <tr>
          <th>#</th>
          <th>Supplier</th>
          <th>Contact</th>
          <th className="text-center" title="Products this supplier carries">
            Products
          </th>
          <th title="Purchase orders made to this supplier">Purchase Orders</th>
          <th className="text-right" title="Unpaid payables to this supplier">
            Payable
          </th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {!paginatedData?.length && (
          <tr>
            <td colSpan={7} className="text-center text-muted py-4">
              No supplier tagged to this branch yet.
            </td>
          </tr>
        )}
        {paginatedData?.map((supplier, index) => {
          const { _id, status, stats } = supplier;
          const info = details(supplier);

          return (
            <tr key={_id}>
              <td>{index + startIndex + 1}</td>
              <td style={{ fontWeight: 400 }}>
                <div className="font-weight-bold">
                  {info.company || info.branch}
                  {info.isGhost && (
                    <MDBBadge
                      color="light"
                      className="ml-1"
                      title="Registered by a branch, not yet a system account"
                    >
                      unverified
                    </MDBBadge>
                  )}
                  {status === "denied" && (
                    <MDBBadge color="danger" className="ml-1">
                      denied
                    </MDBBadge>
                  )}
                </div>
                <small className="d-block text-muted">
                  {info.company && info.branch !== info.company && info.branch}
                  {info.isMain ? " (main)" : ""}
                  {info.abbr ? ` · ${info.abbr}` : ""}
                </small>
              </td>
              <td className="small">
                {info.number ? mobile(info.number) : <Muted>No mobile</Muted>}
                {info.email && <div className="text-muted">{info.email}</div>}
                <div className="text-muted">
                  {info.address || "No address"}
                </div>
              </td>
              <td className="text-center">
                {stats?.products ? (
                  <>
                    <b>{stats.products}</b>
                    {!!stats.preferred && (
                      <small
                        className="d-block text-muted"
                        title="Products that order from this supplier first"
                      >
                        {stats.preferred} preferred
                      </small>
                    )}
                  </>
                ) : (
                  <Muted />
                )}
              </td>
              <td className="small">
                {stats?.orders ? (
                  <>
                    <b>{stats.orders}</b> P.O.
                    {!!stats.openOrders && (
                      <MDBBadge color="warning" className="ml-1">
                        {stats.openOrders} open
                      </MDBBadge>
                    )}
                    <div className="text-muted">
                      Last: {dateFormat(stats.lastOrder)}
                    </div>
                    {!!stats.received && (
                      <div className="text-muted">
                        Received: {currency.format(stats.received)}
                      </div>
                    )}
                  </>
                ) : (
                  <Muted>No P.O. yet</Muted>
                )}
              </td>
              <td className="text-right">
                {stats?.payable ? (
                  <>
                    <b className="text-danger">
                      {currency.format(stats.payable)}
                    </b>
                    <small className="d-block text-muted">
                      {stats.unpaid} unpaid
                    </small>
                  </>
                ) : (
                  <Muted>None</Muted>
                )}
              </td>
              <td className="text-center">
                <MDBBtnGroup>
                  <MDBBtn
                    size="sm"
                    rounded
                    color="primary"
                    onClick={() => handleEdit(supplier)}
                  >
                    <MDBIcon icon="pencil-alt" />
                  </MDBBtn>
                  <MDBBtn
                    onClick={() => handleDelete(supplier._id)}
                    size="sm"
                    rounded
                    color="danger"
                  >
                    <MDBIcon icon="trash" />
                  </MDBBtn>
                </MDBBtnGroup>
              </td>
            </tr>
          );
        })}
      </tbody>
    </MDBTable>
  );
};

export default Body;
